import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { extractError } from "../../lib/format";
import { Loader2, Search, X, UnlockKeyhole } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso; }
}
function bandLabel(id) {
  if (!id) return "\u2014";
  return "Ages " + id.replace("age_", "").replace("-", "\u2013");
}

function UserDrawer({ userId, onClose, onUnlock }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  useEffect(() => {
    let alive = true;
    api.get(`/admin/users/${userId}`)
      .then(({ data }) => alive && setState({ loading: false, data, error: null }))
      .catch((e) => alive && setState({ loading: false, data: null, error: extractError(e, "Could not load user.") }));
    return () => { alive = false; };
  }, [userId]);

  return (
    <div className="ftg-adm__drawer" role="dialog" aria-modal="true" data-testid="user-drawer">
      <div className="ftg-adm__drawer-scrim" onClick={onClose} />
      <aside className="ftg-adm__drawer-panel">
        <header className="ftg-adm__drawer-head">
          <h3 className="ftg-adm__drawer-title">User details</h3>
          <button type="button" onClick={onClose} className="ftg-adm__drawer-close" data-testid="user-drawer-close" aria-label="Close">
            <X size={16} />
          </button>
        </header>
        <div className="ftg-adm__drawer-body">
          {state.loading && <div className="ftg-adm__empty"><Loader2 size={16} className="ftg-spin" /> Loading…</div>}
          {state.error && <div className="ftg-adm__empty">{state.error}</div>}
          {state.data && (
            <>
              <dl className="ftg-adm__kv">
                <div><dt>Email</dt><dd data-testid="user-drawer-email">{state.data.user.email}</dd></div>
                <div><dt>Parent</dt><dd>{state.data.user.parent_name || "\u2014"}</dd></div>
                <div><dt>Child</dt><dd>{state.data.user.child_name || "\u2014"} · {state.data.user.child_age || "\u2014"}</dd></div>
                <div><dt>Country</dt><dd>{state.data.user.country || "\u2014"}</dd></div>
                <div><dt>Joined</dt><dd>{formatDate(state.data.user.created_at)}</dd></div>
              </dl>

              <h4 className="ftg-adm__drawer-h4">Enrollments ({state.data.enrollments.length})</h4>
              {!state.data.enrollments.length && <div className="ftg-adm__muted">No enrollments.</div>}
              {state.data.enrollments.map((e) => (
                <div key={e.id} className="ftg-adm__drawer-card" data-testid={`user-enrollment-${e.id}`}>
                  <div className="ftg-adm__drawer-card-title">{bandLabel(e.age_band)}</div>
                  <div className="ftg-adm__drawer-card-meta">
                    Tier: <strong>{e.tier}</strong> · Terms unlocked: <strong>{e.terms_unlocked.join(", ") || "\u2014"}</strong>
                  </div>
                  <button
                    type="button"
                    onClick={() => onUnlock(e.id)}
                    className="ftg-btn ftg-btn--ghost ftg-btn--sm"
                    data-testid={`user-unlock-${e.id}`}
                    disabled={e.terms_unlocked.length >= 3}
                  >
                    <UnlockKeyhole size={12} /> {e.terms_unlocked.length >= 3 ? "All unlocked" : "Unlock next term"}
                  </button>
                </div>
              ))}

              <h4 className="ftg-adm__drawer-h4">Submissions ({state.data.submissions.length})</h4>
              {!state.data.submissions.length && <div className="ftg-adm__muted">No submissions.</div>}
              {state.data.submissions.slice(0, 8).map((s) => (
                <div key={s.id} className="ftg-adm__drawer-list-row" data-testid={`user-sub-${s.id}`}>
                  <span className="ftg-adm__drawer-list-title">{s.lesson_title}</span>
                  <span className="ftg-adm__pill">{s.ai_grade || "—"}</span>
                </div>
              ))}

              <h4 className="ftg-adm__drawer-h4">Certificates ({state.data.certificates.length})</h4>
              {!state.data.certificates.length && <div className="ftg-adm__muted">None yet.</div>}
              {state.data.certificates.map((c) => (
                <div key={c.id} className="ftg-adm__drawer-list-row" data-testid={`user-cert-${c.cert_id_slug}`}>
                  <span className="ftg-adm__drawer-list-title">{c.cert_type === "level_2" ? "Level 2" : `Level 1 · Term ${c.term}`}</span>
                  <span className="ftg-adm__pill">{c.grade || bandLabel(c.age_band)}</span>
                </div>
              ))}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

export default function AdminUsers() {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [drawerUid, setDrawerUid] = useState(null);
  const LIMIT = 25;

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get(`/admin/users?search=${encodeURIComponent(search)}&skip=${page * LIMIT}&limit=${LIMIT}`);
      setState({ loading: false, data, error: null });
    } catch (e) {
      setState({ loading: false, data: null, error: extractError(e, "Could not load users.") });
    }
  };
  useEffect(() => { load(); }, [page]);

  const onSearch = (e) => {
    e.preventDefault();
    setPage(0);
    load();
  };

  const unlock = async (enrollmentId) => {
    try {
      await api.post(`/enrollments/${enrollmentId}/unlock-next-term`);
      toast.success("Term unlocked");
      setDrawerUid(null);
      await load();
    } catch (e) {
      toast.error(extractError(e, "Could not unlock term."));
    }
  };

  return (
    <div className="ftg-adm__stack" data-testid="users-root">
      <form className="ftg-adm__toolbar" onSubmit={onSearch}>
        <div className="ftg-adm__search">
          <Search size={14} />
          <input
            type="search"
            placeholder="Search email, parent name, or child name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="users-search-input"
          />
        </div>
        <button type="submit" className="ftg-btn ftg-btn--secondary ftg-btn--sm" data-testid="users-search-btn">Search</button>
      </form>

      {state.loading && <div className="ftg-adm__empty" data-testid="users-loading"><Loader2 className="ftg-spin" size={16} /> Loading…</div>}
      {state.error && <div className="ftg-adm__empty" data-testid="users-error">{state.error}</div>}
      {state.data && (
        <>
          <div className="ftg-adm__table-wrap">
            <table className="ftg-adm__table" data-testid="users-table">
              <thead>
                <tr>
                  <th>Parent</th><th>Child</th><th>Email</th><th>Country</th>
                  <th>Band</th><th>Tier</th><th>Terms</th><th>Subs</th><th>Certs</th><th></th>
                </tr>
              </thead>
              <tbody>
                {state.data.items.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td>{u.parent_name}</td>
                    <td>{u.child_name || "\u2014"} <span className="ftg-adm__muted">· {u.child_age || "\u2014"}</span></td>
                    <td className="ftg-adm__mono">{u.email}</td>
                    <td>{u.country || "\u2014"}</td>
                    <td>{bandLabel(u.age_band)}</td>
                    <td><span className={`ftg-adm__pill ${u.tier === "paid" ? "is-paid" : ""}`}>{u.tier || "—"}</span></td>
                    <td>{u.terms_unlocked?.join(",") || "\u2014"}</td>
                    <td>{u.submission_count}</td>
                    <td>{u.certificate_count}</td>
                    <td>
                      <button
                        type="button"
                        className="ftg-btn ftg-btn--ghost ftg-btn--sm"
                        onClick={() => setDrawerUid(u.id)}
                        data-testid={`user-open-${u.id}`}
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
                {!state.data.items.length && (
                  <tr><td colSpan={10} className="ftg-adm__muted">No users match.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="ftg-adm__pager" data-testid="users-pager">
            <button type="button" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="ftg-btn ftg-btn--ghost ftg-btn--sm">Prev</button>
            <span className="ftg-adm__pager-info">
              {page * LIMIT + 1}–{Math.min(state.data.total, (page + 1) * LIMIT)} of {state.data.total}
            </span>
            <button
              type="button"
              disabled={(page + 1) * LIMIT >= state.data.total}
              onClick={() => setPage((p) => p + 1)}
              className="ftg-btn ftg-btn--ghost ftg-btn--sm"
            >
              Next
            </button>
          </div>
        </>
      )}
      {drawerUid && (
        <UserDrawer userId={drawerUid} onClose={() => setDrawerUid(null)} onUnlock={unlock} />
      )}
    </div>
  );
}
